import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import Image from 'next/image';
import { LuX, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import {
  PhonicsStudentStatus,
  PhonicsLessonGroup,
  PhonicsLesson,
  PhonicsActivityTypeMode,
  PhonicsActivity,
  PhonicsStudentActivityStatus,
  useGetPhonicsSoundsQuery,
  useGetPhonicsActivityResponseDetailQuery,
} from '@/api/types';
import { Button, Spinner, Table } from '@/components';
import {
  ManageBreadcrumb,
  ManagePanel,
  ManageYearWeekLessonAccordion,
} from '@/components/manage';
import {
  ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER,
} from '@/components/LessonPlayer/types';
import { findSoundsInString } from '@/utils/sounds';

interface ActivityResponsesDetailProps {
  studentId: string;
  activityId: string;
  text: string;
}

const ActivityResponsesDetail = ({
  studentId,
  activityId,
  text,
}: ActivityResponsesDetailProps) => {
  const { data: responseDetailResult, loading: responseDetailLoading } =
    useGetPhonicsActivityResponseDetailQuery({
      variables: {
        studentId,
        activityId,
        text,
      },
    });

  return responseDetailLoading ? (
    <Spinner />
  ) : (
    <div className="p-3 bg-white rounded-xl shadow-md">
      <Table
        columns={{
          responseDate: {
            label: 'Response Date',
            render: (fieldName, row) =>
              DateTime.fromISO(row.responseDate).toFormat(
                'yyyy-MM-dd HH:mm:ss',
              ),
          },
          score: {
            label: 'Score',
            render: (fieldName, row) => row.score,
          },
          image: {
            label: 'Image',
            render: (fieldName, row) =>
              row.image ? (
                <Image src={row.image} alt="" width="100" height="100" />
              ) : (
                <p>No image data</p>
              ),
          },
        }}
        rows={
          responseDetailResult?.getPhonicsActivityResponseDetail.map(
            (response) => ({
              responseDate: response.createdAt,
              score: response.metaData?.score,
              image: response.metaData?.base64Image,
            }),
          ) || []
        }
        striped
      />
    </div>
  );
};

interface ActivityResponsesProps {
  activity: PhonicsActivity;
  studentActivityStatus: PhonicsStudentActivityStatus;
}

const ActivityResponses = ({
  activity,
  studentActivityStatus,
}: ActivityResponsesProps) => {
  const [detailSymbol, setDetailSymbol] = useState<string | undefined>();

  const { data: soundsResult, loading: soundsLoading } =
    useGetPhonicsSoundsQuery();

  const getResponseUi = (
    activity: PhonicsActivity,
    text: string,
    score: number | null,
  ) => {
    switch (activity.type.identifier) {
      case ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER:
        return (
          <>
            {text} - {score}
          </>
        );
      default:
        return (
          <>
            <span>
              {text} - {score}
            </span>

            <Button
              className="ml-2"
              size="sm"
              onClick={() =>
                setDetailSymbol(detailSymbol === text ? undefined : text)
              }
            >
              View detail
            </Button>

            {detailSymbol === text && (
              <ActivityResponsesDetail
                studentId={studentActivityStatus.studentId}
                activityId={activity.id}
                text={text}
              />
            )}
          </>
        );
    }
  };

  if (soundsLoading) {
    return <Spinner />;
  }

  switch (activity.type.identifier) {
    case ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER:
    case ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER:
      const activitySounds = findSoundsInString(
        soundsResult?.getPhonicsSounds.map((sound) => sound.name) || [],
        activity.sounds || '',
      );

      return activitySounds.map((sound, index) => (
        <div key={index}>
          {getResponseUi(
            activity,
            sound,
            studentActivityStatus.responses?.find(
              (response) => response.text === sound,
            )?.score || null,
          )}
        </div>
      ));

    case ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER:
      return activity.wordSegmentations?.map((segmentation, index) => (
        <div key={index}>
          {getResponseUi(
            activity,
            segmentation,
            studentActivityStatus.responses?.find(
              (response) => response.text === segmentation,
            )?.score || null,
          )}
        </div>
      ));

    case ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER:
      return activity.words?.map((word, index) => (
        <div key={index}>
          {getResponseUi(
            activity,
            word,
            studentActivityStatus.responses?.find(
              (response) => response.text === word,
            )?.score || null,
          )}
        </div>
      ));

    case ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER:
      return activity.sentences?.map((sentence, index) => (
        <div key={index}>
          {getResponseUi(
            activity,
            sentence,
            studentActivityStatus.responses?.find(
              (response) => response.text === sentence,
            )?.score || null,
          )}
        </div>
      ));

    default:
      return null;
  }
};

interface ManageStudentProps {
  studentStatus: PhonicsStudentStatus;
  lessonGroups: Array<PhonicsLessonGroup>;
  currentLessonId: string;
  onBack?: () => void;
}

export const ManageStudent = ({
  studentStatus,
  lessonGroups,
  currentLessonId,
  onBack,
}: ManageStudentProps) => {
  const [lessonGroupsInScope, setLessonGroupsInScope] =
    useState<Array<PhonicsLessonGroup>>();

  const [currentYear, setCurrentYear] = useState<
    PhonicsLessonGroup | undefined
  >();

  const [currentWeek, setCurrentWeek] = useState<
    PhonicsLessonGroup | undefined
  >();

  const [currentLesson, setCurrentLesson] = useState<
    PhonicsLesson | undefined
  >();

  const [openYearGroup, setOpenYearGroup] = useState<
    PhonicsLessonGroup | undefined
  >();

  const [openWeekGroup, setOpenWeekGroup] = useState<
    PhonicsLessonGroup | undefined
  >();

  const [openLesson, setOpenLesson] = useState<PhonicsLesson | undefined>();

  const getLessonsUpToCurrent = (
    groupedLessons: Array<PhonicsLessonGroup>,
    currentLessonId: string,
  ) => {
    const result: Array<PhonicsLessonGroup> = [];

    for (const year of groupedLessons) {
      const newYear: PhonicsLessonGroup = {
        id: year.id,
        name: year.name,
        children: [],
      };

      if (!year.children) {
        continue;
      }

      for (const week of year.children) {
        const newWeek: PhonicsLessonGroup = {
          id: week.id,
          name: week.name,
          lessons: [],
        };

        if (!week.lessons) {
          continue;
        }

        for (const lesson of week.lessons) {
          if (lesson.id === currentLessonId) {
            newWeek.lessons?.push(lesson);
            newYear.children?.push(newWeek);
            result.push(newYear);

            setCurrentYear(newYear);
            setCurrentWeek(newWeek);
            setCurrentLesson(lesson);
            setOpenYearGroup(newYear);
            setOpenWeekGroup(newWeek);
            setOpenLesson(lesson);

            return result;
          }

          newWeek.lessons?.push(lesson);
        }

        newYear.children?.push(newWeek);
      }

      result.push(newYear);
    }

    return result;
  };

  useEffect(() => {
    setLessonGroupsInScope(
      getLessonsUpToCurrent(lessonGroups, currentLessonId),
    );
  }, [lessonGroups, currentLessonId]);

  return (
    lessonGroupsInScope && (
      <div className="flex flex-col gap-3">
        <ManageBreadcrumb section="students" student={studentStatus} />

        <ManagePanel
          bgColorScheme="white"
          actions={[
            ...(onBack
              ? [
                  {
                    label: <LuX className="" />,
                    onClick: () => {},
                  },
                ]
              : []),
          ]}
        >
          <dl>
            <dt className="font-bold">Student:</dt>
            <dd>{studentStatus.name}</dd>

            <dt className="font-bold">Class current lesson:</dt>
            <dd>
              {currentYear?.name}, {currentWeek?.name}, Lesson{' '}
              {currentLesson?.number}
            </dd>
          </dl>
        </ManagePanel>

        <ManageYearWeekLessonAccordion
          lessonGroups={lessonGroupsInScope}
          openYearGroup={openYearGroup}
          setOpenYearGroup={setOpenYearGroup}
          openWeekGroup={openWeekGroup}
          setOpenWeekGroup={setOpenWeekGroup}
          podMode={false}
          onRenderLesson={(lesson, index) => (
            <div
              key={index}
              className="bg-white p-3 rounded-xl flex flex-col gap-3"
            >
              <button
                onClick={() =>
                  setOpenLesson(openLesson === lesson ? undefined : lesson)
                }
              >
                <h4 className="flex gap-2 font-bold text-pt-primary dark:text-pt-lightening-yellow text-left">
                  {openLesson === lesson ? (
                    <LuChevronUp className="w-6 h-6" />
                  ) : (
                    <LuChevronDown className="w-6 h-6" />
                  )}{' '}
                  Lesson {lesson.number}
                </h4>
              </button>

              {openLesson === lesson &&
                lesson.activities
                  .filter(
                    (activity) =>
                      activity.type.mode === PhonicsActivityTypeMode.LEARN,
                  )
                  .map((activity) => {
                    const studentActivityStatus = studentStatus.lessonStatuses
                      .find(
                        (lessonStatus) => lessonStatus.lessonId === lesson.id,
                      )
                      ?.activityStatuses.find(
                        (activityStatus) =>
                          activityStatus.activityId === activity.id,
                      );

                    return (
                      <>
                        <h5 className="font-bold text-pt-primary dark:text-pt-lightening-yellow text-left">
                          {activity.type.name}
                        </h5>

                        {!studentActivityStatus ? (
                          <p>Not completed</p>
                        ) : (
                          <ActivityResponses
                            activity={activity}
                            studentActivityStatus={studentActivityStatus}
                          />
                        )}
                      </>
                    );
                  })}
            </div>
          )}
        />
      </div>
    )
  );
};
